/**
 * VendorDocumentsScreen — /v4/vendor/documents
 *
 * Cross-project document table.
 * Columns: document name, project, type, status, uploaded date
 * Filters: All / Pending / Approved / Rejected
 * Sort: uploaded_at desc (default)
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { FileText, CheckCircle2, Clock, XCircle } from 'lucide-react'
import { fetchMyDocuments } from '../../api/vendor'
import type { VendorDocument } from '../../api/vendor'
import {
  type VendorDocStatusFilter,
  VENDOR_DOC_FILTER_LABELS,
  vendorDocStatusDisplay,
  matchesDocFilter,
  isPendingDocStatus,
} from './vendorDocStatus'

// ── Status helpers ────────────────────────────────────────────────────────

function StatusIcon({ status, paidAt }: { status: string; paidAt?: string | null }) {
  if (paidAt)                    return <CheckCircle2 size={13} color="var(--green)" />
  if (status === 'approved')       return <CheckCircle2 size={13} color="var(--green)" />
  if (status === 'rejected')       return <XCircle      size={13} color="var(--red)"   />
  return                                  <Clock        size={13} color="var(--amber)"  />
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  })
}

function formatSize(bytes: number): string {
  if (bytes < 1024)         return `${bytes} B`
  if (bytes < 1024 * 1024)  return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// ── Filter pill ───────────────────────────────────────────────────────────

function FilterPill({
  value, active, count, onClick,
}: { value: VendorDocStatusFilter; active: boolean; count: number; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '5px 12px',
        borderRadius: 20,
        border: `1px solid ${active ? 'var(--accent)' : 'var(--line)'}`,
        background: active ? 'var(--accent)' : 'var(--surface)',
        color: active ? 'var(--surface)' : 'var(--ink3)',
        fontSize: 12,
        fontWeight: active ? 600 : 400,
        cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: 5,
        transition: 'all 0.12s',
      }}
    >
      {VENDOR_DOC_FILTER_LABELS[value]}
      {count > 0 && (
        <span style={{
          background: active ? 'rgba(255,255,255,0.25)' : 'var(--panel)',
          color: active ? 'var(--surface)' : 'var(--ink4)',
          borderRadius: 10,
          padding: '0px 6px',
          fontSize: 11,
          fontWeight: 600,
        }}>
          {count}
        </span>
      )}
    </button>
  )
}

// ── Document row ──────────────────────────────────────────────────────────

function DocRow({ doc, onProjectClick }: {
  doc: VendorDocument
  onProjectClick: () => void
}) {
  const { label, color } = vendorDocStatusDisplay(doc.status, doc.paid_at)

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      alignItems: 'center',
      padding: '12px 16px',
      borderBottom: '1px solid var(--line)',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, minWidth: 0 }}>
        <div style={{ marginTop: 1, flexShrink: 0 }}>
          <StatusIcon status={doc.status} paidAt={doc.paid_at} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {doc.original_name}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 12px', marginTop: 3, fontSize: 11, color: 'var(--ink4)' }}>
            <button
              onClick={onProjectClick}
              style={{
                fontSize: 11, color: 'var(--ink3)', background: 'none',
                border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline',
                display: 'inline-flex', alignItems: 'center', gap: 3,
              }}
            >
              {doc.project_name}
            </button>
            <span style={{ textTransform: 'capitalize' }}>{doc.doc_type.replace('_', ' ')}</span>
            <span style={{ color }}>{label}</span>
            {doc.uploaded_by_role === 'contractor' && doc.filed_by_company && (
              <span
                data-testid="filed-by-contractor-tag"
                style={{
                  color: 'var(--ink3)',
                  background: 'var(--panel)',
                  border: '1px solid var(--line)',
                  borderRadius: 4,
                  padding: '0 6px',
                  fontWeight: 600,
                }}
              >
                Filed by {doc.filed_by_company}
              </span>
            )}
            {doc.file_size > 0 && <span>{formatSize(doc.file_size)}</span>}
          </div>
        </div>
      </div>
      <span style={{ fontSize: 11, color: 'var(--ink5)', marginLeft: 12, flexShrink: 0 }}>
        {formatDate(doc.uploaded_at)}
      </span>
    </div>
  )
}

// ── Skeleton ──────────────────────────────────────────────────────────────

function SkeletonRow() {
  return (
    <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--line)' }}>
      <div className="skel" style={{ width: '50%', height: 13, marginBottom: 7 }} />
      <div className="skel" style={{ width: '30%', height: 11 }} />
    </div>
  )
}

// ── Main screen ───────────────────────────────────────────────────────────

export default function VendorDocumentsScreen() {
  const [filter, setFilter] = useState<VendorDocStatusFilter>('all')
  const navigate = useNavigate()

  const { data: docs, isLoading, isError } = useQuery({
    queryKey:  ['vendor-my-documents'],
    queryFn:   fetchMyDocuments,
    staleTime: 30_000,
  })

  const filtered = (docs ?? []).filter(d => matchesDocFilter(d.status, filter))

  const counts: Record<VendorDocStatusFilter, number> = {
    all:      (docs ?? []).length,
    pending:  (docs ?? []).filter(d => isPendingDocStatus(d.status)).length,
    approved: (docs ?? []).filter(d => d.status === 'approved').length,
    rejected: (docs ?? []).filter(d => d.status === 'rejected').length,
  }

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 4px' }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)', margin: '0 0 4px' }}>
          Documents
        </h1>
        <p style={{ fontSize: 13, color: 'var(--ink4)', margin: 0 }}>
          All documents you've uploaded across your projects
        </p>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
        {(['all', 'pending', 'approved', 'rejected'] as VendorDocStatusFilter[]).map(s => (
          <FilterPill
            key={s}
            value={s}
            active={filter === s}
            count={s === 'all' ? 0 : counts[s]}
            onClick={() => setFilter(s)}
          />
        ))}
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {/* Table header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          padding: '10px 16px',
          background: 'var(--panel)',
          borderBottom: '1px solid var(--line)',
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--ink4)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}>
          <span>Document</span>
          <span>Date</span>
        </div>

        {isLoading && (
          <>
            <SkeletonRow /><SkeletonRow /><SkeletonRow />
          </>
        )}

        {isError && (
          <div style={{ padding: '20px 16px', fontSize: 13, color: 'var(--red)' }}>
            Failed to load documents. Please refresh.
          </div>
        )}

        {!isLoading && !isError && filtered.length === 0 && (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            padding: '40px 20px', textAlign: 'center',
          }}>
            <FileText size={28} color="var(--ink5)" style={{ marginBottom: 12 }} />
            <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)', margin: '0 0 6px' }}>
              {filter === 'all' ? 'No documents uploaded yet' : `No ${VENDOR_DOC_FILTER_LABELS[filter].toLowerCase()} documents`}
            </p>
            {filter === 'all' && (
              <p style={{ fontSize: 13, color: 'var(--ink4)', margin: 0, maxWidth: 240 }}>
                Upload documents from a project page.
              </p>
            )}
          </div>
        )}

        {filtered.map(doc => (
          <DocRow
            key={doc.id}
            doc={doc}
            onProjectClick={() => navigate(`/vendor/projects/${doc.project_id}`)}
          />
        ))}
      </div>
    </div>
  )
}

